import React, { useRef, useCallback, FormEvent, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { ResourceStrings } from '../../../ResourceStrings';
import { useSelector } from 'react-redux';
import { showIf } from '../../../utils/react';
import { RootState } from '../../../store/rootState';
import { AppState, ReportType } from '../../../store/appState';
import { App } from '../../../app';
import * as Actions from '../../../store/actions';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { EntryValue } from '../../../constants/urlParams';
import { minimumPostDetailsLength } from '../../../constants/validation';
import { testFeedbackTitlePrefix } from '../../../constants/config';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import MarkdownEditor from '../../shared/MarkdownEditor';
import { ReportFeedbackConfig, SpaceConfig } from '../../../config/spaces/spaceConfig';
import _ from 'lodash';
import spaceVSMac from '../../../config/spaces/spaceVSMac';
import { SpaceId } from '../../../config/spaces/spaceIds';

// Do not change the .text strings. They are used as keys in the backend.
// See: https://mseng.visualstudio.com/DefaultCollection/InternalTools/_git/Feedback/pullrequest/564530?_a=files&path=%2FMacaw%2FShared%2FFeedbackScoreLibrary%2FModel%2FScore%2FProblemSeverity%2FSeveritySignal.cs
const blockingSignalOptions = [
    { key: 'CantUse', text: "I'm unable to use this version" }, // Do not change the .text strings. They are used as keys in the backend
    { key: 'FrustratedCanUse', text: "It's more difficult to complete my work" }, // Do not change the .text strings. They are used as keys in the backend
    { key: 'NiceToHave', text: "It bothers me. A fix would be nice" }, // Do not change the .text strings. They are used as keys in the backend
    { key: 'Other', text: "Other" }, // Do not change the .text strings. They are used as keys in the backend
];

// Do not change the .text strings. They are used as keys in the backend
// See: https://mseng.visualstudio.com/DefaultCollection/InternalTools/_git/Feedback/pullrequest/564530?_a=files&path=%2FMacaw%2FShared%2FFeedbackScoreLibrary%2FModel%2FScore%2FProblemSeverity%2FSeveritySignal.cs
const blockingSignalOptionsInternal = [
    { key: 'MsftPersonBlocked', text: "My work is severely impacted or blocked (internal MSFT only)" }, // Do not change the .text strings. They are used as keys in the backend
    { key: 'MsftTeamBlocked', text: "Multiple people on my team are impacted (internal MSFT only)" }, // Do not change the .text strings. They are used as keys in the backend
]

export interface DescriptionEditorProps {
    reportType: ReportType;
    forNeedMoreInfo?: boolean;
    reportConfig: ReportFeedbackConfig;
    spaceConfig: SpaceConfig;
}

export default function DescriptionEditor(props: DescriptionEditorProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const trackingId = useSelector((state: RootState) => state.appState.settings.template.trackingId);
    const refState = useRef(state);
    refState.current = state;

    let refreshDetailPreview: () => void;

    const beforePreview = (markdown: string, stateParam: AppState): string => App.getReportDetails(stateParam, markdown);

    const formatInfoHandler = (action: string): void => {
        Actions.setUsedFormatting(true);
        App.telemetry.postBusinessEvent("formatcommand-used", {
            command: action,
        });
    };

    const chkRegressionChanged = (e: FormEvent<HTMLElement | HTMLInputElement>): void => {
        const state = refState.current;

        App.telemetry.postBusinessEvent("isregression-clicked", {
            selected: (!state.reportIsRegression) ? "Yes" : "No",
        });
        Actions.rapUpdateIsRegression(!state.reportIsRegression);

        // @TODO: Temporary workaround to refresh markdowneditor after state has updated
        setTimeout(() => {
            refreshDetailPreview?.();
        }, 100);
    };

    const whatversionChanged = (whatversionText: string): void => {
        if (refState.current.reportWorkedInText !== whatversionText) {
            Actions.rapSetWorkedInText(whatversionText);
        }
        refreshDetailPreview?.();
    };

    const detailChanged = useCallback((value: string) => {
        if (refState.current.reportDetail !== value) {
            const deferredUpdates = _.debounce((value) => {
                Actions.evUpdateCommentText(value);
                Actions.nmiUpdateCommentText(value);
            }, 100);
            Actions.rapSetDetail(value);
            deferredUpdates(value);
        }
    }, [refState.current.reportDetail]);

    const severityChanged = (item): void => {
        const severityTag = item.text;

        if (refState.current.reportSeverityText !== severityTag) {
            Actions.rapSetSeverityText(severityTag);
        }
        refreshDetailPreview?.();
    }

    const detailsPlaceholder = props.reportConfig?.descriptionPlaceholder
        ?? (state.rapPostType === EntryValue.suggestion ? ResourceStrings.suggestionDescriptionPlaceholder() : ResourceStrings.problemDescriptionPlaceholder());

    const ignoreValidations = state.reportTitle.startsWith(testFeedbackTitlePrefix);
    const isShort = !ignoreValidations && state.reportDetail && state.reportDetail.length < minimumPostDetailsLength;
    const isSameAsTitle = !ignoreValidations && state.reportDetail && state.reportDetail.length >= minimumPostDetailsLength && state.reportDetail === state.reportTitle;
    let detailError: string;
    if (isShort) {
        detailError = ResourceStrings.errorTextLongerThan(minimumPostDetailsLength);
    }
    else if (isSameAsTitle) {
        detailError = ResourceStrings.errorTitleAndDescriptionBeDifferent();
    }

    const severityOptions = App.isMicrosoftAccount(state.userEmail)
        ? blockingSignalOptions.concat(blockingSignalOptionsInternal)
        : blockingSignalOptions;

    const showRegressionOptions = props.reportConfig.showRegressionOptions;
    const showSeverityOptions = props.reportConfig.showSeverityOptions;

    return <Wrapper>
        <MarkdownEditor
            className="editor-wrapper"
            nodeTrackingId={trackingId}
            text={state.reportDetail}
            ariaLabel={ResourceStrings.description()}
            ariaRequired={true}
            placeHolder={detailsPlaceholder}
            formatInfoHandler={formatInfoHandler}
            beforePreview={(markdown: string) => beforePreview(markdown, refState.current)}
            textChanged={detailChanged}
            setRefresh={(refresh) => { refreshDetailPreview = refresh }}
            isInvalid={isShort || isSameAsTitle}
            notificationText={detailError}
        />

        {showSeverityOptions &&
            <Severity>
                <Dropdown
                    placeholder={`${ResourceStrings.chooseHowTheIssueAffectsYou()} ${ResourceStrings.optional()}`}
                    ariaLabel={ResourceStrings.chooseHowTheIssueAffectsYou()}
                    options={severityOptions}
                    onChange={(evt, item) => severityChanged(item)}
                />
            </Severity>
        }

        {showRegressionOptions &&
            <div css="margin-bottom: 2rem">
                <Regression>
                    <Checkbox
                        checked={state.reportIsRegression}
                        onChange={(evt) => chkRegressionChanged(evt)}
                        onRenderLabel={() => <span>
                            <span>{ResourceStrings.problemStartedAfterUpgrade(props.spaceConfig?.displayName)}&nbsp;</span>
                            <span className="deemphasized-text">{ResourceStrings.optional()}</span>
                        </span>}
                    />
                </Regression>
                <div style={showIf(state.reportIsRegression)}>
                    <label htmlFor="whatversion">
                        <b css="padding-top: .4rem; display: block; font-size: 1.4rem">{ResourceStrings.whenDidItLastWork()}</b>
                    </label>
                    <WhatVersion
                        id="whatversion"
                        type="text"
                        minLength={1}
                        maxLength={40}
                        value={state.reportWorkedInText}
                        placeholder={ResourceStrings.whenDidItLastWork_Example()}
                        onChange={(evt) => whatversionChanged(evt.target.value)} />
                </div>
            </div>
        }
    </Wrapper>
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    border: solid 1px var(--color-on-surface);
    border-radius: .2rem;
    padding-bottom: 2rem;

    >* {
        padding: 0 2rem;
    }

    >* + * {
        margin-top: 2rem;
    }

    #reportDetail-messages {
        margin-top: .5rem;
    }

    .ms-ChoiceField + .ms-ChoiceField {
        margin-left: 3rem;
    }

    .editor-wrapper {
        padding: 0;

        .detail {
            padding: 1.5rem 2.5rem;
        }

        .html-preview {
            padding: 2rem 2.5rem;
        }
    }
`;

const Regression = styled.div`
    &, span {
        font-size: 1.4rem;
    }
`;

const Severity = styled.div`
    .severity-label {
        margin-bottom: 0.4rem;
    }
`;

const WhatVersion = styled.input`
    width: 24rem;
    margin-right: .5rem;
    padding-left: 0.3rem;
    max-width: 100%;
`;
